<template>
  <a-layout class="module-layout">
    <!-- 侧边栏导航 -->
    <a-layout-sider width="240" class="sidebar" :trigger="null" collapsible>
      <div class="sidebar-title">{{ moduleTitle }}</div>
      <a-menu
        v-model:selected-keys="selectedKeys"
        :open-keys="openKeys"
        mode="inline"
        class="sidebar-menu"
        @click="handleMenuClick"
        @open-change="handleOpenChange"
      >
        <template v-for="item in menuItems" :key="item.key">
          <a-menu-item v-if="!item.children" :key="`menu_${item.key}`">
            <template v-if="isValidIcon(item.icon)" #icon>
              <component :is="item.icon" />
            </template>
            <span>{{ item.title }}</span>
          </a-menu-item>
          <a-sub-menu v-else :key="`submenu_${item.key}`">
            <template v-if="isValidIcon(item.icon)" #icon>
              <component :is="item.icon" />
            </template>
            <template #title>{{ item.title }}</template>
            <a-menu-item v-for="child in item.children" :key="child.key" @click.stop="handleSubMenuClick(child)">
              {{ child.title }}
            </a-menu-item>
          </a-sub-menu>
        </template>
      </a-menu>
    </a-layout-sider>

    <!-- 主内容区 -->
    <a-layout-content class="main-content">
      <div :key="reloadKey">
        <slot />
      </div>
    </a-layout-content>
  </a-layout>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

interface MenuItem {
  key: string
  title: string
  icon?: any
  path?: string
  children?: MenuItem[]
}

interface Props {
  moduleTitle: string
  menuItems: MenuItem[]
  reloadKey?: string | number
}

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()

// 选中菜单
const selectedKeys = ref<string[]>([])
const openKeys = ref<string[]>([])

// 查找当前路由对应的菜单项
const findActiveMenu = () => {
  const currentPath = route.path
  
  // 遍历菜单项，找到匹配的路径
  const findInItems = (items: MenuItem[]): string | null => {
    if (!Array.isArray(items)) {
      return null
    }
    for (const item of items) {
      // 精确匹配路径
      if (item.path === currentPath) {
        return item.key
      }
      // 检查子菜单
      if (item.children) {
        const childKey = findInItems(item.children)
        if (childKey) {
          // 设置父菜单打开
          if (!openKeys.value.includes(item.key)) {
            openKeys.value.push(item.key)
          }
          return childKey
        }
      }
    }
    return null
  }
  
  const activeKey = findInItems(props.menuItems)
  if (activeKey) {
    selectedKeys.value = [activeKey]
  } else {
    selectedKeys.value = []
  }
}

// 菜单点击处理
const handleMenuClick = ({ key }: { key: string }) => {
  // 移除可能的前缀
  const cleanKey = key.replace(/^(menu_|submenu_)/, '')
  
  // 递归查找路径
  const findPath = (items: MenuItem[]): string | null => {
    for (const item of items) {
      if (item.key === cleanKey || item.key === key) {
        return item.path || null
      }
      if (item.children) {
        const path = findPath(item.children)
        if (path) return path
      }
    }
    return null
  }
  
  const path = findPath(props.menuItems)
  if (path) {
    router.push(path)
  }
}

// 子菜单项点击处理
const handleSubMenuClick = (child: MenuItem) => {
  if (child.path) {
    router.push(child.path)
  }
}

// 子菜单展开处理
const handleOpenChange = (keys: string[]) => {
  openKeys.value = keys
}

// 监听路由变化
watch(() => route.path, () => {
  findActiveMenu()
}, { immediate: true })

// 监听菜单项变化，重新计算选中项
watch(() => props.menuItems, () => {
  findActiveMenu()
}, { deep: true })

// 验证图标是否有效
function isValidIcon(icon: any): boolean {
  return icon && (typeof icon === 'function' || (typeof icon === 'object' && icon.render))
}
</script>

<style scoped lang="scss">
.module-layout {
  height: 100%;
  width: 100%;
}

.sidebar {
  background: #fff;
  border-right: 1px solid #f0f0f0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .sidebar-title {
    height: 60px;
    line-height: 60px;
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    color: #262626;
    border-bottom: 1px solid #f0f0f0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 10px;
    background: #fafafa;
  }

  .sidebar-menu {
    border: none;
    flex: 1;
    overflow-y: auto;

    :deep(.ant-menu-item),
    :deep(.ant-menu-submenu-title) {
      font-size: 14px;
      height: 48px;
      line-height: 48px;
      display: flex;
      align-items: center;

      .anticon {
        width: 18px;
        height: 18px;
        margin-right: 8px;
        flex-shrink: 0;
      }

      span {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.main-content {
  background: #f5f5f5;
  padding: 20px;
  overflow-y: auto;
  min-width: 0;
}
</style>
